import { useNavigate } from 'react-router-dom'
import { Spinner, useCurrentUser } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { TabBar } from '../components/tab-bar'
import { txDisplay, useCoinBalance, useRecentTransactions } from '../_lib/wallet-api'

export function WalletPage() {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const { data: wallet, isLoading: balLoading } = useCoinBalance(user?.user_id)
  const { data: txs, isLoading: txLoading } = useRecentTransactions(wallet?.id)

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <TopBar title="我的钱包" />

        <main data-testid="Yk8X4" className="flex flex-1 flex-col gap-5 px-6 pt-3 pb-safe-tab">
          <section className="rounded-2xl bg-gradient-to-br from-[#19caad] via-[#22b9ad] to-[#1d9be0] p-5 text-white shadow-[0_12px_32px_rgba(25,202,173,0.25)]">
            <p className="text-[12px] uppercase tracking-wider opacity-80">COIN 余额</p>
            <p className="mt-2 flex items-baseline gap-1 text-[40px] font-bold leading-tight tabular-nums">
              {balLoading ? <Spinner /> : (wallet?.balance ? Number(wallet.balance).toLocaleString() : '0')}
              <span className="text-[14px] font-normal opacity-90">金币</span>
            </p>
            <p className="mt-1 text-[12px] opacity-85">
              ≈ ¥{wallet?.balance ? (Number(wallet.balance) / 100).toFixed(2) : '0.00'}（100 金币 = 1 元）
            </p>
          </section>

          <div className="flex gap-3">
            <ActionBtn icon="recharge" label="充值" primary onClick={() => navigate('/wallet/recharge')} />
            <ActionBtn icon="transfer" label="转账" onClick={() => navigate('/wallet/transfer')} />
            <ActionBtn icon="withdraw" label="提现" onClick={() => navigate('/wallet/withdraw')} />
          </div>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-[#1A1A1A]">流水记录</h2>
              <button
                type="button"
                onClick={() => navigate('/orders')}
                className="text-[12px] text-[#19caad]"
              >
                查看全部
              </button>
            </div>
            {txLoading ? (
              <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
            ) : txs && txs.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {txs.map((tx) => {
                  const { isOut, label, amountAbs } = txDisplay(tx)
                  return (
                    <li
                      key={tx.id}
                      className="flex items-center gap-3 rounded-xl bg-white px-4 py-3"
                    >
                      <span
                        className="flex h-9 w-9 items-center justify-center rounded-full"
                        style={{ background: isOut ? '#FFE4E4' : '#E5F8E8', color: isOut ? '#FF6B6B' : '#19B665' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d={isOut ? 'M7 17L17 7M17 7H8M17 7v9' : 'M17 7L7 17M7 17h9M7 17V8'} /></svg>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[14px] font-medium text-[#1A1A1A]">{label}</p>
                        <p className="text-[11px] text-[#9CA3AF]">{tx.created_at.slice(0, 16).replace('T', ' ')}</p>
                      </div>
                      <span className={`text-[14px] font-semibold tabular-nums ${isOut ? 'text-[#FF6B6B]' : 'text-[#19B665]'}`}>
                        {isOut ? '-' : '+'}{amountAbs}
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="rounded-xl bg-white py-8 text-center text-[13px] text-[#9CA3AF]">暂无流水</div>
            )}
          </section>
        </main>

        <TabBar />
      </MobileScreen>
    </AppLayout>
  )
}

function ActionBtn({
  icon,
  label,
  primary,
  onClick,
}: {
  icon: 'recharge' | 'transfer' | 'withdraw'
  label: string
  primary?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        primary
          ? 'flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-[#19caad] text-[14px] font-semibold text-white active:bg-[#14a18b]'
          : 'flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-white text-[14px] font-semibold text-[#1A1A1A] active:bg-[#F1F2F4]'
      }
    >
      <ActionIcon name={icon} />
      {label}
    </button>
  )
}

function ActionIcon({ name }: { name: 'recharge' | 'transfer' | 'withdraw' }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  if (name === 'recharge') return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>
  if (name === 'transfer') return <svg {...common}><path d="M3 11l4-4M3 11l4 4M3 11h14M21 18l-4-4M21 18l-4 4M21 18H7" /></svg>
  return <svg {...common}><path d="M12 4v12M5 11l7 7 7-7" /></svg>
}
